import { useState } from 'react';
import {
	Box,
	TextField,
	IconButton,
	Button,
	InputAdornment,
	Tooltip
} from '@mui/material';
import {
	Visibility,
	VisibilityOff,
	Refresh as RefreshIcon,
	ContentCopy as CopyIcon
} from '@mui/icons-material';

interface PasswordGeneratorProps {
	value: string;
	onChange: (password: string) => void;
	label?: string;
	disabled?: boolean;
}

const generatePassword = (length: number = 12): string => {
	const lowercase = 'abcdefghijklmnopqrstuvwxyz';
	const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
	const numbers = '0123456789';
	const symbols = '!@#$%^&*';

	const allChars = lowercase + uppercase + numbers + symbols;

	let password = '';
	password += lowercase[Math.floor(Math.random() * lowercase.length)];
	password += uppercase[Math.floor(Math.random() * uppercase.length)];
	password += numbers[Math.floor(Math.random() * numbers.length)];
	password += symbols[Math.floor(Math.random() * symbols.length)];

	for (let i = password.length; i < length; i++) {
		password += allChars[Math.floor(Math.random() * allChars.length)];
	}

	return password.split('').sort(() => Math.random() - 0.5).join('');
};

export const PasswordGenerator: React.FC<PasswordGeneratorProps> = ({
	value,
	onChange,
	label = 'Mật khẩu',
	disabled = false
}) => {
	const [showPassword, setShowPassword] = useState(false);
	const [copied, setCopied] = useState(false);

	const handleGeneratePassword = () => {
		const newPassword = generatePassword(12);
		onChange(newPassword);
	};

	const handleCopyPassword = async () => {
		if (value) {
			await navigator.clipboard.writeText(value);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		}
	};

	const handleTogglePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};

	return (
		<Box>
			<TextField
				fullWidth
				label={label}
				type={showPassword ? 'text' : 'password'}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				disabled={disabled}
				InputProps={{
					endAdornment: (
						<InputAdornment position="end">
							<Tooltip title={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}>
								<IconButton
									onClick={handleTogglePasswordVisibility}
									edge="end"
									size="small"
								>
									{showPassword ? <VisibilityOff /> : <Visibility />}
								</IconButton>
							</Tooltip>
							<Tooltip title={copied ? 'Đã sao chép!' : 'Sao chép mật khẩu'}>
								<span>
									<IconButton
										onClick={handleCopyPassword}
										disabled={!value}
										edge="end"
										size="small"
										color={copied ? 'success' : 'default'}
									>
										<CopyIcon />
									</IconButton>
								</span>
							</Tooltip>
						</InputAdornment>
					)
				}}
			/>
			<Box sx={{ mt: 1 }}>
				<Button
					variant="outlined"
					size="small"
					startIcon={<RefreshIcon />}
					onClick={handleGeneratePassword}
					disabled={disabled}
				>
					Tạo mật khẩu ngẫu nhiên
				</Button>
			</Box>
		</Box>
	);
};
